import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a variant's flavor label from metafield custom.flavor_label.
 * This component listens for variant update events and updates the flavor display accordingly.
 * Uses morphing to update the component with server-rendered HTML for accurate metafield values.
 */
class FlavorLabelComponent extends HTMLElement {
  connectedCallback() {
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section, dialog');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateFlavorLabel);
  }

  disconnectedCallback() {
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section, dialog');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateFlavorLabel);
  }

  /**
   * Updates the flavor label by morphing in the new HTML from the server response.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateFlavorLabel = (event) => {
    // Handle product changes in combined listings
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the matching flavor-label-component in the new HTML using block ID
    const blockId = this.dataset.blockId;
    let newFlavorLabel;

    if (blockId) {
      newFlavorLabel = event.detail.data.html.querySelector(`flavor-label-component[data-block-id="${blockId}"]`);
    }

    // Fallback to first flavor-label-component if no block ID match
    if (!newFlavorLabel) {
      newFlavorLabel = event.detail.data.html.querySelector('flavor-label-component');
    }

    if (!newFlavorLabel) return;

    // Update display style based on whether there's flavor content
    const newFlavorValue = newFlavorLabel.querySelector('[ref="flavorValue"]');
    const hasContent = newFlavorValue && newFlavorValue.textContent.trim() !== '';

    this.style.display = hasContent ? 'block' : 'none';

    // Morph only the children to preserve event listeners on the component itself
    morph(this, newFlavorLabel, { childrenOnly: true });
  };
}

if (!customElements.get('flavor-label-component')) {
  customElements.define('flavor-label-component', FlavorLabelComponent);
}
